import { useEffect, useState } from 'react';
import {
  generatePath,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  ajvResolver,
  apiAbortController,
  ButtonPrimary,
  ButtonSecondary,
  ClickOutsideDetector,
  IconLoader,
  InputText,
  Popup,
  ResponseError,
  responseErrorsResolver,
  Select,
  SelectItemProps,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  ExternalOrganizationFormValues,
  OrganizationType,
  PspIntegrationOrganization,
  PspIntegrationPayment,
} from '../types';
import {
  useBindExternalOrganizationMutation,
  useLazyGetPspIntegrationsOrganizationsByNameQuery,
} from '../store/services/customers';
import { LinkCustomerFormTypes } from './types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const schema = {
  type: 'object',
  properties: {
    externalId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'External Customer is required',
    },
    pspPartnerId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Bank is required',
    },
  },
  required: ['externalId', 'pspPartnerId'],
};

let timeout: NodeJS.Timeout;

const LinkCustomerForm = ({
  type,
  setExternalPanelIsOpen,
  selectedPartner,
  linkExternalCustomerPopupVisible,
  setLinkExternalCustomerPopupVisible,
  parent,
  selectedPspIntegrationId: selectedPspPartnerIntegrationId,
}: LinkCustomerFormTypes) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [selectedPspIntegrationId, setSelectedPspIntegrationId] = useState('');
  const [availablePspPartners, setAvailablePspPartners] = useState<
    SelectItemProps[]
  >([]);
  const [pspIntegrationOrganizationsList, setPspIntegrationOrganizationsList] =
    useState<PspIntegrationOrganization[]>([]);
  const [
    externalOrganizationsListVisible,
    setExternalOrganizationsListVisible,
  ] = useState(false);
  const [externalOrganizationSearch, setExternalOrganizationSearch] =
    useState('');

  const { pspIntegrations, pspPartners } = useAppSelector(
    (state) => state.customersExternalAccounts,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );

  const navigate = useNavigate();

  const { customerId } = useParams();
  const { search } = useLocation();

  const [bindExternalOrganization, { isLoading, isSuccess }] =
    useBindExternalOrganizationMutation();
  const [
    getPspIntegrationOrganizations,
    { isLoading: organizationsIsLoading, isFetching: organizationsIsFetching },
  ] = useLazyGetPspIntegrationsOrganizationsByNameQuery();

  const baseInfo =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;

  const customerName =
    type === OrganizationType.INDIVIDUAL
      ? `${(baseInfo as Partial<GeneralInfo>)?.firstName} ${(baseInfo as Partial<GeneralInfo>)?.lastName}`
      : (baseInfo as Partial<CorporateGeneralInfo>)?.name;

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = useForm<ExternalOrganizationFormValues>({
    resolver: ajvResolver(schema),
  });

  const pspPartnerId = watch('pspPartnerId');
  const externalId = watch('externalId');

  const onBindExternalOrganization = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await bindExternalOrganization({
      organizationId: customerId as string,
      organizationType: type,
      externalId: values?.externalId,
      active: true,
      pspPartnerId: values.pspPartnerId,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const getPspIntegrationOrganizationsAction = async ({
    name,
  }: {
    name: string;
  }) => {
    apiAbortController.abortPendingRequest();

    const response = await getPspIntegrationOrganizations(
      `?pspIntegrationId=${selectedPspIntegrationId || selectedPspPartnerIntegrationId}&name=${name}&take=40`,
    );

    if (response?.data?.data) {
      const nextOrganizationsList = response?.data?.data;

      setPspIntegrationOrganizationsList(nextOrganizationsList);
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setSelectedPspIntegrationId('');
    setExternalOrganizationsListVisible(false);
    setPspIntegrationOrganizationsList([]);
    setExternalOrganizationSearch('');
    setGlobalError('');
    setDirtyPopupVisible(false);

    if (!setExternalPanelIsOpen) {
      let link = '';

      if (type === OrganizationType.INDIVIDUAL) {
        link = generatePath(
          ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS,
          {
            customerId: customerId || '',
          },
        );
      } else {
        link = generatePath(
          ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS,
          {
            customerId: customerId || '',
          },
        );
      }

      setTimeout(() => {
        navigate(`${link}${search || ''}`);
      }, 100);
    }

    if (setExternalPanelIsOpen) {
      setExternalPanelIsOpen(true);
    }

    if (setLinkExternalCustomerPopupVisible) {
      setLinkExternalCustomerPopupVisible(false);
    }
  };

  useEffect(() => {
    if (pspPartners && pspIntegrations) {
      const nextPspPartners = pspPartners.map((item) => {
        const result = { ...item };

        if (
          result.pspIntegrationId &&
          pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          )
        ) {
          const currentItegration = pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          );

          if (currentItegration) {
            result.pspIntegration = {
              name: currentItegration.name,
              pspIntegrationPayments:
                currentItegration.pspIntegrationPayments as PspIntegrationPayment[],
              id: currentItegration.id,
              externalCustomersSupported:
                currentItegration.externalCustomersSupported,
            };
          }
        }

        return result;
      });

      const pspPartnersWithExternalCustomerSupport = nextPspPartners?.filter(
        (item) => item?.pspIntegration?.externalCustomersSupported,
      );

      if (pspPartnersWithExternalCustomerSupport?.length) {
        setAvailablePspPartners(
          pspPartnersWithExternalCustomerSupport?.map((item) => ({
            name: item.companyName,
            value: item.id,
          })),
        );
      }
    }
  }, [pspPartners, pspIntegrations, selectedPartner]);

  useEffect(() => {
    if (pspPartnerId && availablePspPartners && pspIntegrations) {
      const currentPartner = pspPartners?.find(
        (item) => item.id === pspPartnerId || item.id === selectedPartner,
      );

      if (currentPartner && currentPartner?.pspIntegrationId) {
        const currentIntegration = pspIntegrations?.find(
          (item) => item.id === currentPartner?.pspIntegrationId,
        );

        if (currentIntegration) {
          setSelectedPspIntegrationId(currentIntegration.id);
        }
      }
    }
  }, [pspPartnerId, availablePspPartners, pspIntegrations, selectedPartner]);

  useEffect(() => {
    if (
      externalOrganizationSearch &&
      (organizationsIsLoading || organizationsIsFetching)
    ) {
      setExternalOrganizationsListVisible(true);
    }
  }, [
    externalOrganizationSearch,
    organizationsIsLoading,
    organizationsIsFetching,
  ]);

  useEffect(() => {
    if (!setExternalPanelIsOpen) {
      setPanelIsOpen(true);
    }
  }, [setExternalPanelIsOpen]);

  useEffect(() => {
    if (linkExternalCustomerPopupVisible) {
      setPanelIsOpen(true);

      if (selectedPartner) {
        setValue('pspPartnerId', selectedPartner);
      }
    }
  }, [linkExternalCustomerPopupVisible, selectedPartner]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <>
      <SidePanel
        title="Link Client"
        isOpen={panelIsOpen}
        type={SidePanelType.AML}
        parent={parent}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        sidepanelExpandedKey="link-external-organization-panel"
        errorSidepanel={globalError}
        footer={
          <>
            <ButtonPrimary
              text={isLoading ? 'Loading...' : 'Link'}
              disabled={!externalId || isLoading}
              onClick={onBindExternalOrganization}
              bold
            />
            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        <>
          <div className={cn.pspIntegrationSelect}>
            <Controller
              control={control}
              defaultValue=""
              name="pspPartnerId"
              render={({ field: { name, value, onChange } }) => (
                <Select
                  name={name}
                  list={availablePspPartners}
                  value={selectedPartner || value}
                  title="Bank"
                  placeholder="Select Bank"
                  onChange={onChange}
                  error={errors?.pspPartnerId?.message}
                  disabled={Boolean(selectedPartner)}
                />
              )}
            />
          </div>
          <div className={cn.pspIntegrationSelect}>
            <Controller
              control={control}
              defaultValue=""
              name="organizationId"
              render={({ field: { name } }) => (
                <InputText
                  name={name}
                  value={customerName}
                  title="Customer"
                  placeholder="Enter name..."
                  error={errors?.organizationId?.message}
                  disabled
                />
              )}
            />
          </div>
          <div className={cn.pspIntegrationSelect}>
            <Controller
              control={control}
              defaultValue=""
              name="externalId"
              render={({ field: { onChange } }) => (
                <ClickOutsideDetector
                  setOptionsListVisible={setExternalOrganizationsListVisible}
                >
                  <div className={cn.searchExternalOrganizationWrapper}>
                    <InputText
                      name="searchName"
                      value={externalOrganizationSearch}
                      title="External Customer"
                      placeholder="Enter name..."
                      onChange={(e) => {
                        clearTimeout(timeout);

                        setExternalOrganizationSearch(e.target.value);

                        if (e.target.value) {
                          timeout = setTimeout(() => {
                            getPspIntegrationOrganizationsAction({
                              name: e.target.value,
                            });
                          }, 700);
                        }
                      }}
                      disabled={!pspPartnerId}
                      onFocus={() => {
                        if (pspIntegrationOrganizationsList?.length) {
                          setExternalOrganizationsListVisible(true);
                        }
                      }}
                      error={errors?.externalId?.message}
                    />
                    {externalOrganizationsListVisible && (
                      <div className={cn.organizationList}>
                        {(organizationsIsLoading ||
                          organizationsIsFetching) && (
                          <div className={cn.organizationListLoader}>
                            <IconLoader />
                          </div>
                        )}
                        {!organizationsIsLoading &&
                          !organizationsIsFetching &&
                          pspIntegrationOrganizationsList?.length > 0 &&
                          pspIntegrationOrganizationsList?.map((item) => (
                            <button
                              type="button"
                              className={cn.organizationButton}
                              key={item.externalId}
                              onClick={() => {
                                setExternalOrganizationsListVisible(false);
                                onChange(item.externalId);
                                setExternalOrganizationSearch(item.name);
                              }}
                              disabled={Boolean(item.organizationType !== type)}
                            >
                              <div className={cn.organizationButtonLeft}>
                                {item.name}
                              </div>
                              <div className={cn.organizationButtonRight}>
                                {item.organizationType
                                  .split('_')
                                  ?.map(
                                    (organizationType) =>
                                      `${organizationType.charAt(0)}${organizationType.slice(1).toLowerCase()}`,
                                  )
                                  .join(' ')}
                              </div>
                              <div className={cn.externalOrganizationId}>
                                {item.externalId}
                              </div>
                              {item.organizationType !== type && (
                                <div className={cn.wrongType}>
                                  The external customer type does not match the
                                  current customer type
                                </div>
                              )}
                            </button>
                          ))}
                        {!pspIntegrationOrganizationsList?.length &&
                          !organizationsIsLoading &&
                          !organizationsIsFetching && (
                            <div className={cn.organizationButton}>
                              No customer yet
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </ClickOutsideDetector>
              )}
            />
          </div>
        </>
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('external-dirty-popup') as HTMLElement}
      />
      <div id="external-dirty-popup" />
    </>
  );
};

export default LinkCustomerForm;
